import { useEffect, useState } from 'react';
import { api, type Gift } from '@/lib/api';
import { GIFT_VIDEO_PRELOAD_CACHE_KEY, GIFT_VIDEO_PRELOAD_CACHE_VERSION } from '@/app/const/locale';

interface PreloadStatus {
  loading: boolean;
  loaded: number;
  total: number;
  error: string | null;
  progress: number; // 0-100
}

interface CachedVideoInfo {
  url: string;
  timestamp: number; // 缓存时间戳
  version: string; // 缓存版本
}

interface VideoCache {
  version: string;
  videos: Record<string, CachedVideoInfo>; // key: videoUrl, value: 缓存信息
  lastUpdate: number; // 最后更新时间
}

// 全局单例状态管理器
class GiftVideoPreloadManager {
  private static instance: GiftVideoPreloadManager | null = null;
  private status: PreloadStatus = {
    loading: false,
    loaded: 0,
    total: 0,
    error: null,
    progress: 0,
  };
  private listeners: Set<(status: PreloadStatus) => void> = new Set();
  private preloadPromise: Promise<void> | null = null;
  private videoElements: Map<string, HTMLVideoElement> = new Map();
  private loadedUrls: Set<string> = new Set();
  private cachedUrls: Set<string> = new Set(); // 从 localStorage 读取的已缓存视频

  private constructor() {
    // 初始化时从 localStorage 读取缓存
    this.loadCache();
  }

  static getInstance(): GiftVideoPreloadManager {
    if (!GiftVideoPreloadManager.instance) {
      GiftVideoPreloadManager.instance = new GiftVideoPreloadManager();
    }
    return GiftVideoPreloadManager.instance;
  }

  // 订阅状态变化
  subscribe(listener: (status: PreloadStatus) => void): () => void {
    this.listeners.add(listener);
    // 立即通知当前状态
    listener(this.status);
    // 返回取消订阅函数
    return () => {
      this.listeners.delete(listener);
    };
  }

  // 通知所有订阅者
  private notify() {
    this.listeners.forEach(listener => listener(this.status));
  }

  // 更新状态
  private updateStatus(updates: Partial<PreloadStatus>) {
    this.status = { ...this.status, ...updates };
    this.notify();
  }

  // 开始预加载（确保只执行一次）
  async startPreload(): Promise<void> {
    // 如果已经在加载或已完成，直接返回
    if (this.preloadPromise) {
      return this.preloadPromise;
    }

    // 如果已经完成，直接返回
    if (this.status.progress === 100 && !this.status.loading) {
      return Promise.resolve();
    }

    // 创建预加载 Promise
    this.preloadPromise = this.doPreload();
    return this.preloadPromise;
  }

  private async doPreload(): Promise<void> {
    try {
      this.updateStatus({ loading: true, error: null });

      // 获取礼物列表
      const response = await api.gift.getGifts();
      const gifts: Gift[] = response.data || [];

      // 过滤出有视频的礼物
      const videoGifts = gifts.filter(gift => gift.giftVideo && gift.giftVideo.trim() !== '');
      if (videoGifts.length === 0) {
        this.updateStatus({
          loading: false,
          loaded: 0,
          total: 0,
          error: null,
          progress: 100,
        });
        return;
      }

      // 统计已缓存的视频数量
      const cachedCount = videoGifts.filter(gift => this.isVideoCached(gift.giftVideo)).length;
      let loadedCount = cachedCount;

      this.updateStatus({
        total: videoGifts.length,
        loaded: loadedCount,
        progress: videoGifts.length > 0 ? Math.round((loadedCount / videoGifts.length) * 100) : 0,
      });

      // 预加载每个视频
      const preloadPromises = videoGifts.map((gift) => {
        return new Promise<void>((resolve) => {
          // 如果已经在内存中加载过，直接跳过
          if (this.loadedUrls.has(gift.giftVideo)) {
            // 已经在初始化时统计过了，不需要再次增加计数
            resolve();
            return;
          }

          // 如果已经在 localStorage 中缓存过，直接跳过（不需要重新加载）
          if (this.isVideoCached(gift.giftVideo)) {
            // 标记为已加载（但不创建 video 元素）
            // 注意：已经在初始化时统计过了，不需要再次增加计数
            this.loadedUrls.add(gift.giftVideo);
            resolve();
            return;
          }

          // 标记为已加载
          this.loadedUrls.add(gift.giftVideo);

          // 使用 video 元素预加载（确保视频真正加载到浏览器缓存）
          this.preloadWithVideo(gift.giftVideo, () => {
            // 保存到缓存
            this.saveVideoToCache(gift.giftVideo);
            console.log('预加载礼物视频:', gift.giftVideo);
            loadedCount++;
            this.updateStatus({
              loaded: loadedCount,
              progress: Math.round((loadedCount / videoGifts.length) * 100),
            });
            resolve();
          }, () => {
            // 加载失败
            loadedCount++;
            this.updateStatus({
              loaded: loadedCount,
              progress: Math.round((loadedCount / videoGifts.length) * 100),
            });
            resolve();
          });
        });
      });

      // 等待所有视频加载完成
      await Promise.all(preloadPromises);

      this.updateStatus({
        loading: false,
        progress: 100,
      });
    } catch (error) {
      console.error('预加载礼物视频失败:', error);
      this.updateStatus({
        loading: false,
        error: error instanceof Error ? error.message : '未知错误',
      });
    }
  }

  // 获取当前状态
  getStatus(): PreloadStatus {
    return { ...this.status };
  }

  // 从 localStorage 加载缓存
  private loadCache(): void {
    try {
      const cached = localStorage.getItem(GIFT_VIDEO_PRELOAD_CACHE_KEY);
      if (cached) {
        const cache: VideoCache = JSON.parse(cached);
        // 检查缓存版本
        if (cache.version === GIFT_VIDEO_PRELOAD_CACHE_VERSION) {
          // 将缓存的视频 URL 添加到 loadedUrls 和 cachedUrls
          Object.keys(cache.videos).forEach(url => {
            this.loadedUrls.add(url);
            this.cachedUrls.add(url);
          });
        } else {
          // 版本不匹配，清理旧缓存
          localStorage.removeItem(GIFT_VIDEO_PRELOAD_CACHE_KEY);
        }
      }
    } catch (error) {
      console.warn('读取礼物视频缓存失败:', error);
      // 如果读取失败，清理可能损坏的缓存
      try {
        localStorage.removeItem(GIFT_VIDEO_PRELOAD_CACHE_KEY);
      } catch (e) {
        // 忽略清理错误
      }
    }
  }

  // 保存视频到 localStorage 缓存
  private saveVideoToCache(url: string): void {
    try {
      let cache: VideoCache;
      const cached = localStorage.getItem(GIFT_VIDEO_PRELOAD_CACHE_KEY);
      
      if (cached) {
        cache = JSON.parse(cached);
        // 检查版本
        if (cache.version !== GIFT_VIDEO_PRELOAD_CACHE_VERSION) {
          cache = {
            version: GIFT_VIDEO_PRELOAD_CACHE_VERSION,
            videos: {},
            lastUpdate: Date.now(),
          };
        }
      } else {
        cache = {
          version: GIFT_VIDEO_PRELOAD_CACHE_VERSION,
          videos: {},
          lastUpdate: Date.now(),
        };
      }

      // 添加或更新视频缓存
      cache.videos[url] = {
        url,
        timestamp: Date.now(),
        version: GIFT_VIDEO_PRELOAD_CACHE_VERSION,
      };
      cache.lastUpdate = Date.now();

      // 保存到 localStorage
      localStorage.setItem(GIFT_VIDEO_PRELOAD_CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      // localStorage 可能已满或不可用，尝试清理一些旧数据
      console.warn('保存礼物视频缓存失败:', error);
      this.cleanOldCache();
    }
  }

  // 清理旧的缓存数据（当存储空间不足时）
  private cleanOldCache(): void {
    try {
      const cached = localStorage.getItem(GIFT_VIDEO_PRELOAD_CACHE_KEY);
      if (cached) {
        const cache: VideoCache = JSON.parse(cached);
        const videos = Object.values(cache.videos);
        
        // 按时间戳排序，保留最新的 50 个视频
        videos.sort((a, b) => b.timestamp - a.timestamp);
        const keepVideos = videos.slice(0, 50);
        
        const newCache: VideoCache = {
          version: GIFT_VIDEO_PRELOAD_CACHE_VERSION,
          videos: {},
          lastUpdate: Date.now(),
        };
        
        keepVideos.forEach(video => {
          newCache.videos[video.url] = video;
        });
        
        localStorage.setItem(GIFT_VIDEO_PRELOAD_CACHE_KEY, JSON.stringify(newCache));
      }
    } catch (error) {
      // 如果清理也失败，直接删除缓存
      console.warn('清理缓存失败，删除缓存:', error);
      try {
        localStorage.removeItem(GIFT_VIDEO_PRELOAD_CACHE_KEY);
      } catch (e) {
        // 忽略
      }
    }
  }

  // 检查视频是否已在缓存中
  private isVideoCached(url: string): boolean {
    return this.cachedUrls.has(url) || this.loadedUrls.has(url);
  }

  // 使用 video 元素预加载视频（确保视频真正加载到浏览器缓存）
  private preloadWithVideo(url: string, onComplete: () => void, onError: () => void): void {
    // 创建隐藏的 video 元素
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    video.style.display = 'none';
    video.style.position = 'absolute';
    video.style.width = '1px';
    video.style.height = '1px';
    video.style.opacity = '0';
    video.style.pointerEvents = 'none';

    // 添加到 DOM（某些浏览器需要元素在 DOM 中才能预加载）
    document.body.appendChild(video);

    // 保存引用
    this.videoElements.set(url, video);

    let isCompleted = false;
    const cleanup = () => {
      if (isCompleted) return;
      isCompleted = true;
      video.removeEventListener('canplaythrough', handleCanPlayThrough);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
      video.removeEventListener('progress', handleProgress);
    };

    // 监听加载完成 - 使用 canplaythrough 确保视频可以播放到结束
    const handleCanPlayThrough = () => {
      cleanup();
      onComplete();
    };

    // 备用：如果 canplaythrough 不触发，使用 canplay + progress
    const handleCanPlay = () => {
      // 当可以播放时，继续监听进度，确保数据被加载
      if (video.readyState >= 3) { // HAVE_FUTURE_DATA
        // 尝试加载更多数据
        video.currentTime = 0.1; // 稍微播放一点，确保数据被缓存
      }
    };

    // 监听加载进度，确保视频数据被真正加载
    const handleProgress = () => {
      // 当加载了足够的数据时（readyState >= 3 表示有未来数据）
      if (video.readyState >= 3 && video.buffered.length > 0) {
        // 检查是否加载了至少 10% 的数据，或者可以播放到结束
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        const duration = video.duration || 0;
        
        if (duration > 0 && bufferedEnd >= duration * 0.1) {
          // 已经加载了至少 10% 的数据，可以认为缓存成功
          cleanup();
          onComplete();
        }
      }
    };

    // 监听加载错误
    const handleError = (e: Event) => {
      console.warn(`预加载礼物视频失败: ${url}`, e);
      cleanup();
      onError();
    };

    // 优先监听 canplaythrough（表示可以播放到结束）
    video.addEventListener('canplaythrough', handleCanPlayThrough, { once: true });
    // 备用监听 canplay
    video.addEventListener('canplay', handleCanPlay, { once: true });
    // 监听进度，确保数据被加载
    video.addEventListener('progress', handleProgress);
    video.addEventListener('error', handleError, { once: true });

    // 设置视频源并开始加载
    video.src = url;
    
    // 触发加载（某些浏览器需要）
    video.load();

    // 设置超时，避免一直等待
    setTimeout(() => {
      if (!isCompleted && video.readyState >= 2) { // HAVE_CURRENT_DATA
        // 至少加载了一些数据，可以认为缓存成功
        cleanup();
        onComplete();
      }
    }, 10000); // 10秒超时
  }
}

/**
 * 预加载直播间礼物视频的 Hook（全局单例）
 * 从接口获取礼物列表，并预加载所有礼物的视频文件
 * 无论在哪里调用，都只会加载一次，所有调用者共享同一个状态
 * 
 * @param enabled 是否启用预加载，默认为 true
 * @returns 预加载状态
 */
export function useGiftVideoPreload(enabled: boolean = true) {
  const [status, setStatus] = useState<PreloadStatus>({
    loading: false,
    loaded: 0,
    total: 0,
    error: null,
    progress: 0,
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const manager = GiftVideoPreloadManager.getInstance();
    
    // 订阅状态变化
    const unsubscribe = manager.subscribe((newStatus) => {
      setStatus(newStatus);
    });

    // 开始预加载（如果还没开始的话）
    manager.startPreload();

    // 清理订阅
    return () => {
      unsubscribe();
    };
  }, [enabled]);

  return status;
}

